/**
 * Where signing in lands you.
 *
 * Customer Management home, by request (Taylor, 2026-08-12) — it replaced
 * `/expense/reports`, which was only ever the landing because Expense was the one
 * built module in phase 0 and the redirect was never revisited.
 *
 * WHY THIS IS A COMPONENT AND NOT A ONE-LINE <Navigate to="/crm/home">.
 * `/crm/home` requires the `view:home` authority. A hard-coded redirect would send
 * an Expense-only user — or a Hotel-Supplier-only user like Stacie, who holds
 * `view:supplier_tab` and NOT `view:home` — straight to a page their role cannot
 * open, on every single sign-in. Landing on a dead end reads as "the app is
 * broken", and it would be the first thing they see.
 *
 * So the preference is expressed as a preference: go to Customer Management home
 * when the user can reach it, otherwise to the first page they actually can. The
 * registry (features/admin/lib/page-registry) already knows which pages a role
 * opens and is the same source the rail and the admin screen read, so this cannot
 * disagree with what the nav shows.
 *
 * FAILS OPEN, matching gatePlatformNav. While /api/whoami is in flight or has
 * errored, `useStoredRoles()` returns undefined and we send everyone to the
 * requested default rather than guessing a fallback. Access is enforced
 * server-side; a redirect that lands somewhere the user is entitled to but we
 * could not yet confirm is strictly better than parking them on a placeholder.
 */
import { Navigate } from 'react-router';
import { useStoredRoles } from '@/auth/useWhoami';
import { useAdminMe } from '@/features/admin/data-access/useAdminMe';
import { pagesForRoles } from '@/features/admin/lib/page-registry';

/** The requested default landing page. */
export const DEFAULT_LANDING = '/crm/home';

/**
 * Resolve the landing path for a set of stored roles.
 *
 * Exported and pure so the preference order is testable without a router: the
 * interesting cases are all "which of several plausible pages wins", and that is
 * invisible from the component.
 */
export function landingPathFor(
  storedRoles: readonly string[] | undefined,
  isAdmin: boolean,
): string {
  // Roles unavailable (in flight / errored) → fail open to the default. Also the
  // admin case: an admin can reach everything, so the preference always holds.
  if (storedRoles === undefined || isAdmin) return DEFAULT_LANDING;

  const reachable = pagesForRoles(storedRoles);
  if (reachable.some((p) => p.path === DEFAULT_LANDING)) return DEFAULT_LANDING;

  // First page in registry order — the registry is declared in rail order then
  // tab order, so "first reachable" is the top-left thing the user can see, which
  // is the least surprising place to land.
  const first = reachable[0];
  if (first) return first.path;

  // No reachable page at all. Send them to the default anyway rather than
  // inventing a route: the module will render its own "no access" state, which is
  // a truthful answer, and the alternative is a redirect loop.
  return DEFAULT_LANDING;
}

export function LandingRedirect() {
  const { data: adminMe } = useAdminMe();
  const storedRoles = useStoredRoles();
  return (
    <Navigate
      replace
      to={landingPathFor(storedRoles, adminMe?.admin === true)}
    />
  );
}
